/**
 * Cookie consent — single source of truth for whether the visitor has
 * given permission to fire non-essential cookies / trackers.
 *
 * Storage: `localStorage` under `neighborli_cookie_consent`. Value is one of
 *   - "accepted"    → all optional categories on (legacy shorthand)
 *   - "declined"    → all optional categories off (legacy shorthand)
 *   - "customized"  → per-category granular choice; details live in
 *                     `neighborli_cookie_consent_categories` as JSON
 *                     { analytics: bool }
 * or null if the visitor hasn't chosen yet.
 * Also stores `neighborli_cookie_consent_at` (ISO timestamp) for audit.
 *
 * Design goals:
 *   - Zero external deps.
 *   - Any part of the app can `import { hasConsent } from './cookieConsent'`
 *     to conditionally load trackers without triggering a re-render.
 *   - Emits a `neighborli:consent-changed` DOM event so listeners
 *     (Google Analytics, Nano-Banana, etc.) can react in real time.
 *   - `hasConsent()` is defined as "may we fire analytics?" — the only
 *     optional category today; adding new categories is a matter of
 *     extending DEFAULT_CATEGORIES.
 */

import { POLICY_VERSION } from './privacyPolicy';
import api from '../api';

const STORAGE_KEY            = 'neighborli_cookie_consent';
const STORAGE_KEY_AT         = 'neighborli_cookie_consent_at';
const STORAGE_KEY_VERSION    = 'neighborli_cookie_consent_version';
const STORAGE_KEY_CATEGORIES = 'neighborli_cookie_consent_categories';
const CONSENT_EVENT          = 'neighborli:consent-changed';

// Optional cookie categories the visitor can opt in/out of. "essential"
// isn't listed because it can't be turned off.
export const DEFAULT_CATEGORIES = { analytics: false };
export const OPTIONAL_CATEGORY_KEYS = Object.keys(DEFAULT_CATEGORIES);

function readCategories() {
  if (typeof window === 'undefined') return { ...DEFAULT_CATEGORIES };
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CATEGORIES);
    if (!raw) return { ...DEFAULT_CATEGORIES };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_CATEGORIES,
      ...Object.fromEntries(OPTIONAL_CATEGORY_KEYS.map(k => [k, !!parsed?.[k]])),
    };
  } catch { return { ...DEFAULT_CATEGORIES }; }
}

/**
 * Return the raw stored decision IF the visitor consented to the CURRENT
 * policy version. When the policy version was bumped after their previous
 * consent, we treat them as un-consented so the banner reappears — GDPR-
 * audit friendly and prevents silent tracking under updated terms.
 */
export function getConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const decision = localStorage.getItem(STORAGE_KEY);
    if (!['accepted', 'declined', 'customized'].includes(decision)) return null;
    const acceptedVersion = localStorage.getItem(STORAGE_KEY_VERSION);
    if (acceptedVersion !== POLICY_VERSION) return null;
    return decision;
  } catch { return null; }
}

/** Return { decision, categories, version, at } for the raw stored record. */
export function getConsentRecord() {
  if (typeof window === 'undefined') return null;
  try {
    const decision = localStorage.getItem(STORAGE_KEY);
    if (!decision) return null;
    return {
      decision,
      categories: readCategories(),
      version: localStorage.getItem(STORAGE_KEY_VERSION) || null,
      at: localStorage.getItem(STORAGE_KEY_AT) || null,
      current_version: POLICY_VERSION,
      is_stale: localStorage.getItem(STORAGE_KEY_VERSION) !== POLICY_VERSION,
    };
  } catch { return null; }
}

/** Fast boolean check used by analytics + third-party gates. Returns true
 *  when the visitor has explicitly enabled analytics — either via
 *  "accepted" (all-on) or a "customized" choice with analytics=true. */
export function hasConsent() {
  const record = getConsentRecord();
  if (!record) return false;
  if (record.is_stale) return false;
  if (record.decision === 'accepted') return true;
  if (record.decision === 'declined') return false;
  if (record.decision === 'customized') return !!record.categories?.analytics;
  return false;
}

/** True when the visitor picked ANY optional category (per-category API). */
export function getCategories() {
  const record = getConsentRecord();
  if (!record || record.is_stale) return { ...DEFAULT_CATEGORIES };
  if (record.decision === 'accepted') {
    return Object.fromEntries(OPTIONAL_CATEGORY_KEYS.map(k => [k, true]));
  }
  if (record.decision === 'declined') {
    return { ...DEFAULT_CATEGORIES };
  }
  return record.categories || { ...DEFAULT_CATEGORIES };
}

/** Fire-and-forget push of the current localStorage record to the server so
 *  the choice follows the user across devices. No-op when unauthenticated
 *  or when the network is unavailable — the client stays canonical either
 *  way and the server is best-effort. */
function pushToBackend(decision, categories) {
  // Only attempt when a bearer token is set (i.e. the user is signed in).
  const hasToken = !!(api?.defaults?.headers?.common?.Authorization);
  if (!hasToken) return;
  api.put('/settings/cookie-consent', {
    decision,
    categories: categories || {},
    version: POLICY_VERSION,
  }).catch(() => { /* server is authoritative-lag; client already saved locally */ });
}

/** Persist an all-on / all-off decision. */
export function setConsent(decision) {
  if (typeof window === 'undefined') return;
  if (!['accepted', 'declined'].includes(decision)) return;
  try {
    localStorage.setItem(STORAGE_KEY, decision);
    localStorage.setItem(STORAGE_KEY_AT, new Date().toISOString());
    localStorage.setItem(STORAGE_KEY_VERSION, POLICY_VERSION);
    localStorage.removeItem(STORAGE_KEY_CATEGORIES);
  } catch { /* localStorage may be blocked; the banner will just reappear next visit */ }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { decision } }));
  pushToBackend(decision, {});
}

/** Persist a per-category choice. */
export function setConsentCategories(categories) {
  if (typeof window === 'undefined') return;
  const cleaned = Object.fromEntries(
    OPTIONAL_CATEGORY_KEYS.map(k => [k, !!categories?.[k]])
  );
  try {
    localStorage.setItem(STORAGE_KEY, 'customized');
    localStorage.setItem(STORAGE_KEY_AT, new Date().toISOString());
    localStorage.setItem(STORAGE_KEY_VERSION, POLICY_VERSION);
    localStorage.setItem(STORAGE_KEY_CATEGORIES, JSON.stringify(cleaned));
  } catch { /* ignore */ }
  window.dispatchEvent(
    new CustomEvent(CONSENT_EVENT, { detail: { decision: 'customized', categories: cleaned } })
  );
  pushToBackend('customized', cleaned);
}

/** Called after login. Merges the server-side stored consent into localStorage
 *  so the user's cross-device choice is honored on this device.
 *
 *  Conflict rules:
 *    - If server has a record and the current policy version matches it,
 *      the server always wins — the intent is "follow me across devices".
 *    - If the server has no record but we do locally, push ours up so
 *      future devices inherit it.
 *    - If neither side has a record, do nothing (banner will show).
 */
export function hydrateFromBackend(serverConsent) {
  if (typeof window === 'undefined') return;
  const local = getConsentRecord();
  if (serverConsent && serverConsent.decision) {
    // Only trust the server record when it was made under the current policy
    // version — otherwise we'd silently accept stale terms.
    if (serverConsent.version && serverConsent.version !== POLICY_VERSION) return;
    try {
      localStorage.setItem(STORAGE_KEY, serverConsent.decision);
      localStorage.setItem(STORAGE_KEY_AT, serverConsent.at || new Date().toISOString());
      localStorage.setItem(STORAGE_KEY_VERSION, POLICY_VERSION);
      if (serverConsent.decision === 'customized' && serverConsent.categories) {
        const cleaned = Object.fromEntries(
          OPTIONAL_CATEGORY_KEYS.map(k => [k, !!serverConsent.categories?.[k]])
        );
        localStorage.setItem(STORAGE_KEY_CATEGORIES, JSON.stringify(cleaned));
      } else {
        localStorage.removeItem(STORAGE_KEY_CATEGORIES);
      }
    } catch { /* ignore */ }
    window.dispatchEvent(
      new CustomEvent(CONSENT_EVENT, {
        detail: { decision: serverConsent.decision, categories: serverConsent.categories || {} },
      })
    );
    return;
  }
  // Server has nothing yet — if we have a local record, push it up so it
  // follows the user going forward.
  if (local && local.decision && !local.is_stale) {
    pushToBackend(
      local.decision,
      local.decision === 'customized' ? (local.categories || {}) : {}
    );
  }
}

/** Test-only helper. Clears the stored decision so the banner reappears. */
export function resetConsent() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY_AT);
    localStorage.removeItem(STORAGE_KEY_VERSION);
    localStorage.removeItem(STORAGE_KEY_CATEGORIES);
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { decision: null } }));
}

export const CONSENT_CHANGED_EVENT = CONSENT_EVENT;
